import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PlusCircle, Pencil, Trash2, Search, MoreHorizontal, Eye } from "lucide-react";
import MastersLayout from "@/components/masters/MastersLayout";
import TablePagination from "@/components/masters/TablePagination";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { categoryApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Loader2 } from "lucide-react";

const CATEGORY_STATUSES = ["Active", "Inactive"];

// Updated Category type to match API response
type Category = {
  id: string;
  organizationId: string;
  name: string;
  code: string;
  status: boolean;
  createdAt: string;
  updatedAt: string;
};

const ITEMS_PER_PAGE = 10;

const CategoriesPage = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<{
    name: string;
    code: string;
    status: boolean;
  }>({
    name: "",
    code: "",
    status: true,
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { toast } = useToast();
  const { user } = useAuth();

  // Load data from API on initial render and when filters/pagination change
  useEffect(() => {
    fetchCategories();
  }, [currentPage, filters.query]);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const response = await categoryApi.getCategories({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        query: filters.query || undefined,
      });

      setCategories(response.docs);
      setTotalDocs(response.totalDocs);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message
          ? error.message
          : "Failed to fetch categories",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate total pages based on totalDocs from API
  const totalPages = Math.ceil(totalDocs / ITEMS_PER_PAGE);

  // Handle filter changes
  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === "name") {
      // Capitalize first letter of each word
      const capitalizedValue = (value as string)
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
      setFormData({ ...formData, [field]: capitalizedValue });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleAdd = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      await categoryApi.createCategory({
        ...formData,
        organizationId: user?.organizationId || "",
      });

      fetchCategories(); // Refresh the list
      setFormData({ name: "", code: "", status: true });
      setShowForm(false);
      toast({
        title: "Success",
        description: "Category added successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ? error.message : "Failed to add category",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim() || !editingId) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      await categoryApi.updateCategory(editingId, {
        ...formData,
        organizationId: user?.organizationId || "",
      });

      fetchCategories(); // Refresh the list
      setFormData({ name: "", code: "", status: true });
      setEditingId(null);
      setShowForm(false);
      toast({
        title: "Success",
        description: "Category updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message
          ? error.message
          : "Failed to update category",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (category: Category) => {
    setFormData({
      name: category.name.trim(),
      code: category.code.trim(),
      status: category.status,
    });
    setEditingId(category.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    setCategoryToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      setIsLoading(true);
      await categoryApi.deleteCategory(categoryToDelete);

      fetchCategories(); // Refresh the list
      toast({
        title: "Success",
        description: "Category deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message
          ? error.message
          : "Failed to delete category",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Trim fields before submission
    const trimmedData = {
      ...formData,
      name: formData.name.trim(),
      code: formData.code.trim(),
    };

    if (!trimmedData.name) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    if (editingId) {
      handleUpdate();
    } else {
      handleAdd();
    }
  };

  return (
    <MastersLayout
    // title="Categories"
    // description="Manage system categories and their configurations"
    >
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search categories..."
                value={filters.query}
                onChange={(e) => handleFilterChange("query", e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <Button
              variant="outline"
              onClick={() => {
                setFilters({ query: "" });
                setCurrentPage(1);
              }}
            >
              Reset
            </Button>
          </div>
        </div>

        {/* <Card> */}
          {/* <CardHeader> */}
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Categories</CardTitle>
                <CardDescription>
                  Manage system categories and their configurations
                </CardDescription>
              </div>
              <Button onClick={() => setShowForm(true)}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Category
              </Button>
            </div>
          {/* </CardHeader> */}
          {/* <CardContent> */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead style={{ minWidth: "200px" }}>
                    Description
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-10">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-10">
                      No categories found
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell className="font-medium whitespace-nowrap">
                        {category.name}
                      </TableCell>
                      <TableCell className="w-auto md:w-1/3 lg:w-2/5 overflow-hidden">
                        <div className="break-words whitespace-normal line-clamp-3">
                          {category.code}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            id={`status-${category.id}`}
                            checked={category.status}
                            onCheckedChange={async (checked) => {
                              try {
                                setIsLoading(true);
                                await categoryApi.updateCategory(category.id, {
                                  ...category,
                                  status: checked,
                                });
                                fetchCategories(); // Refresh the list
                                toast({
                                  title: "Success",
                                  description: `Category status updated to ${
                                    checked ? "Active" : "Inactive"
                                  }`,
                                });
                              } catch (error) {
                                toast({
                                  title: "Error",
                                  description: error.message
                                    ? error.message
                                    : "Failed to update category status",
                                  variant: "destructive",
                                });
                              } finally {
                                setIsLoading(false);
                              }
                            }}
                          />
                          <span>{category.status ? "Active" : "Inactive"}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Open menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem
                              onClick={() => handleEdit(category)}
                              className="cursor-pointer"
                            >
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDelete(category.id)}
                              className="cursor-pointer text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {categories.length > 0 && (
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          {/* </CardContent> */}
        {/* </Card> */}
      </div>

      {showForm && (
        <Dialog
          open={showForm}
          onOpenChange={(open) => {
            if (!open) {
              setFormData({ name: "", code: "", status: true });
              setEditingId(null);
            }
            setShowForm(open);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingId ? "Edit Category" : "Add New Category"}
              </DialogTitle>
              <DialogDescription>
                {editingId
                  ? "Update the category information below"
                  : "Fill in the category information below"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Enter category name"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="code">Description</Label>
                <textarea
                  id="code"
                  value={formData.code}
                  onChange={(e) => handleInputChange("code", e.target.value)}
                  placeholder="Enter category description"
                  // required
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="status">Status</Label>
                <div className="flex items-center gap-2">
                  <Switch
                    id="status"
                    checked={formData.status}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, status: checked })
                    }
                  />
                  <span>{formData.status ? "Active" : "Inactive"}</span>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setFormData({ name: "", code: "", status: true });
                    setEditingId(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingId ? "Update" : "Add"} Category
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure want to delete the category.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MastersLayout>
  );
};

export default CategoriesPage;
